import {
  PlusCircle,
  Pencil,
  Trash2,
  ListPlus,
  Clock,
  DollarSign,
  Users,
  User,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  Loader,
  GraduationCap,
} from "lucide-react";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Badge } from "src/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";

const PriceCard = ({
  filteredPrices,
  searchTerm,
  strings,
  setIsAddDialogOpen,
  setIsFeatureDialogOpen,
  setIsDeleteDialogOpen,
  setSelectedPrice,
  prepareFormForEdit,
  btnLoading,
  handleToggleStatus,
  formatCurrency,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredPrices.length === 0 ? (
        <div className="col-span-full flex flex-col items-center justify-center p-8 text-center">
          <div className="rounded-full bg-muted p-3">
            <DollarSign className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">
            {strings.noPricesFoundTitle}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {searchTerm
              ? strings.noPricesFoundDescSearch
              : strings.noPricesFoundDescDefault}
          </p>
          <Button className="mt-4" onClick={() => setIsAddDialogOpen(true)}>
            <PlusCircle className="mx-2 h-4 w-4" />
            {strings.addNewPrice}
          </Button>
        </div>
      ) : (
        filteredPrices.map((price) => (
          <Card key={price.id} className="overflow-hidden h-fit">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle>{price.name}</CardTitle>
                <Badge variant={price.status === 1 ? "default" : "secondary"}>
                  {price.status === 1 ? strings.active : strings.inactive}
                </Badge>
              </div>
              <CardDescription>
                {price.type === "individual" ? (
                  <div className="flex items-center">
                    <User className="h-3 w-3 mx-1" />
                    {strings.individual}
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Users className="h-3 w-3 mx-1" />
                    {strings.group(price.group_num || 0)}
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="text-2xl font-bold">
                  {formatCurrency(price.price, price.currency)}
                </div>
                {price.discount > 0 && (
                  <Badge variant="outline" className="text-green-600">
                    {price.discount}% OFF
                  </Badge>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-muted-foreground">
                  <Clock className="h-4 w-4 mx-2" />
                  {strings.durationLabel} {price.duration}
                </div>
                <div className="flex items-center text-muted-foreground">
                  <GraduationCap className="h-4 w-4 mx-2" />
                  {strings.LessonLabel} {price.lesson_num}
                </div>
                <div className="flex items-center text-muted-foreground">
                  <DollarSign className="h-4 w-4 mx-2" />
                  {strings.currencyLabel} {price.currency.code}
                </div>
              </div>
              {price.price_feature.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">{strings.featuresLabel}</p>
                  <ul className="text-sm space-y-1">
                    {price.price_feature.slice(0, 2).map((feature) => (
                      <li key={feature.id} className="flex items-start">
                        <CheckCircle className="h-3 w-3 mx-2 text-green-600 mt-1 flex-shrink-0" />
                        <span className="text-muted-foreground">
                          {feature.description}
                        </span>
                      </li>
                    ))}
                    {price.price_feature.length > 2 && (
                      <li className="text-xs text-muted-foreground italic">
                        +{price.price_feature.length - 2} more features
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedPrice(price);
                  setIsFeatureDialogOpen(true);
                }}
              >
                <ListPlus className="h-4 w-4 mx-2" />
                {strings.featuresLabel.replace(":", "")}
              </Button>
              <div className="flex space-x-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>{strings.actions}</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => prepareFormForEdit(price)}>
                      <Pencil className="h-4 w-4 mx-2" />
                      {strings.edit}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleToggleStatus(price)}
                      disabled={btnLoading.toggleStatus}
                    >
                      {btnLoading.toggleStatus ? (
                        <Loader className="animate-spin h-4 w-4 mx-2" />
                      ) : price.status === 1 ? (
                        <>
                          <XCircle className="h-4 w-4 mx-2" />
                          {strings.deactivate}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mx-2" />
                          {strings.activate}
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        setSelectedPrice(price);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mx-2" />
                      {strings.delete}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardFooter>
          </Card>
        ))
      )}
    </div>
  );
};

export default PriceCard;
